import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ShieldCheck } from "lucide-react";

// Local typed wrapper for the beta `supabase.auth.oauth` namespace.
type OAuthApi = {
  getAuthorizationDetails: (id: string) => Promise<{
    data: {
      client?: { name?: string; client_id?: string };
      redirect_uri?: string;
      scope?: string;
      redirect_url?: string;
      redirect_to?: string;
    } | null;
    error: { message: string } | null;
  }>;
  approveAuthorization: (id: string) => Promise<{
    data: { redirect_url?: string; redirect_to?: string } | null;
    error: { message: string } | null;
  }>;
  denyAuthorization: (id: string) => Promise<{
    data: { redirect_url?: string; redirect_to?: string } | null;
    error: { message: string } | null;
  }>;
};
function oauthApi(): OAuthApi {
  return (supabase.auth as unknown as { oauth: OAuthApi }).oauth;
}

export const Route = createFileRoute("/.lovable/oauth/consent")({
  ssr: false,
  validateSearch: (s: Record<string, unknown>) => ({
    authorization_id:
      typeof s.authorization_id === "string" ? s.authorization_id : "",
  }),
  beforeLoad: async ({ search, location }) => {
    if (!search.authorization_id) throw new Error("Missing authorization_id");
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      const next = location.pathname + location.searchStr;
      throw redirect({ to: "/auth", search: { redirect: next } as any });
    }
  },
  loader: async ({ location }) => {
    const authorizationId = new URLSearchParams(location.search).get(
      "authorization_id",
    )!;
    const { data, error } = await oauthApi().getAuthorizationDetails(
      authorizationId,
    );
    if (error) throw new Error(error.message);
    const immediate = data?.redirect_url ?? data?.redirect_to;
    if (immediate && !data?.client) throw redirect({ href: immediate });
    return data;
  },
  component: Consent,
  errorComponent: ({ error }) => (
    <main className="container-narrow px-4 py-16">
      <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
        <h1 className="font-display text-2xl text-navy">
          Authorization unavailable
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          We couldn't load this authorization request:{" "}
          {String((error as Error)?.message ?? error)}
        </p>
      </div>
    </main>
  ),
});

function Consent() {
  const details = Route.useLoaderData();
  const { authorization_id } = Route.useSearch();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function decide(approve: boolean) {
    setBusy(true);
    setError(null);
    const api = oauthApi();
    const { data, error } = approve
      ? await api.approveAuthorization(authorization_id)
      : await api.denyAuthorization(authorization_id);
    if (error) {
      setBusy(false);
      setError(error.message);
      return;
    }
    const target = data?.redirect_url ?? data?.redirect_to;
    if (!target) {
      setBusy(false);
      setError("No redirect returned by the authorization server.");
      return;
    }
    window.location.href = target;
  }

  const clientName = details?.client?.name ?? "an app";

  return (
    <main className="container-narrow px-4 py-12 sm:py-16">
      <div className="mx-auto max-w-md rounded-2xl border border-border bg-card p-6 shadow-soft sm:p-8">
        <div className="flex items-center gap-2 text-gold">
          <ShieldCheck className="h-5 w-5" />
          <span className="text-[11px] uppercase tracking-[0.3em]">
            Authorize access
          </span>
        </div>
        <h1 className="mt-2 font-display text-2xl text-navy sm:text-3xl">
          Connect {clientName} to your account
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          This lets <strong>{clientName}</strong> use the Nkadimeng Danny Legal
          & Compliance Support app as you. It can call this app's enabled tools
          while you are signed in.
        </p>
        {details?.scope && (
          <p className="mt-3 rounded-md bg-muted/40 p-3 text-xs text-muted-foreground">
            Requested permissions: <code>{details.scope}</code>
          </p>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          This does not bypass this app's permissions or backend policies.
        </p>
        {error && (
          <p
            role="alert"
            className="mt-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900"
          >
            {error}
          </p>
        )}
        <div className="mt-6 flex gap-3">
          <Button
            type="button"
            className="h-11 flex-1 bg-navy hover:bg-navy/90"
            disabled={busy}
            onClick={() => decide(true)}
          >
            {busy ? "Please wait…" : "Approve"}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-11 flex-1"
            disabled={busy}
            onClick={() => decide(false)}
          >
            Cancel connection
          </Button>
        </div>
      </div>
    </main>
  );
}
